import { useState } from 'react'
import type { FieldDef } from '../../types'
import { InventorySystemTypes } from '../../types'
import { useInventorySystemProfiles } from '../../hooks/useInventorySystemProfiles'
import { useTenantGroups } from '../../hooks/useTenantGroups'
import { InventorySystemProfilesPicker } from '../../components/InventorySystemProfilesPicker/InventorySystemProfilesPicker'
import { buildVenueInventorySystemProfileLinks } from './helpers/buildVenueInventorySystemProfileLinks'
import { CreateEntityPage } from '../CreateEntityPage/CreateEntityPage'
import { createVenueWithSlugId } from './helpers/createVenueWithSlugId'
import { slugifyVenueName } from '../../helpers/slugifyVenueName'

const ispTypeOptions = Object.values(InventorySystemTypes).map((v) => ({
  value: v,
  label: v,
}))

const statusOptions = [
  { value: 'OKAY', label: 'OKAY' },
  { value: 'OFFSALE', label: 'OFFSALE' },
]

const fields: FieldDef[] = [
  { name: 'sysVenueId', label: 'Sys Venue ID', type: 'text' },
  {
    name: 'name',
    label: 'Name',
    type: 'text',
    deriveValues: (v) => ({ venueSlug: slugifyVenueName(String(v)) }),
  },
  { name: 'venueSlug', label: 'Venue Slug', type: 'text' },
  {
    name: 'status',
    label: 'Status',
    type: 'select',
    options: statusOptions,
    defaultValue: 'OKAY',
  },
  { name: 'timezone', label: 'Timezone', type: 'text' },
  { name: 'latitude', label: 'Latitude', type: 'number' },
  { name: 'longitude', label: 'Longitude', type: 'number' },
  {
    name: 'type',
    label: 'Type',
    type: 'select',
    options: [{ value: 'theatre', label: 'theatre' }],
    defaultValue: 'theatre',
  },
  {
    name: 'inventorySystemType',
    label: 'ISP Type',
    type: 'select',
    options: ispTypeOptions,
    defaultValue: 'lineup',
  },
]

const venueNote = (
  <span>
    <strong>Note</strong>: Used to manage static venues and placeholders (e.g.
    AV inventory system profiles) to enable tenant config.
  </span>
)

export function CreateVenuePage() {
  const {
    profiles,
    isLoading: profilesLoading,
    error: profilesError,
  } = useInventorySystemProfiles()
  const {
    tenantGroups,
    isLoading: groupsLoading,
    error: groupsError,
  } = useTenantGroups()
  const [selectedProfileIds, setSelectedProfileIds] = useState<string[]>([])

  async function createVenueWithLinks(
    token: string,
    body: Record<string, unknown>
  ) {
    await createVenueWithSlugId(token, {
      ...body,
      inventorySystemProfileToVenue: buildVenueInventorySystemProfileLinks(
        selectedProfileIds,
        profiles
      ),
    })
  }

  return (
    <CreateEntityPage
      title="Create Venue"
      backTo="/venues"
      loadingLabel="inventory system profiles"
      isLoading={profilesLoading || groupsLoading}
      loadError={profilesError ?? groupsError}
      note={venueNote}
      fields={fields}
      createFn={createVenueWithLinks}
      extraRow={{
        label: 'Inventory System Profiles',
        isRequired: true,
        content: (
          <InventorySystemProfilesPicker
            profiles={profiles}
            tenantGroups={tenantGroups}
            selectedIds={selectedProfileIds}
            onChange={setSelectedProfileIds}
          />
        ),
      }}
      isExtraRowValid={selectedProfileIds.length > 0}
    />
  )
}
